import { EXPENSE_CATEGORIES, categoryRunRates, type BudgetLine, type CategorySpendMonth, type ExpenseCategory } from "@torre/expenses";
import { budgetLinesFor } from "./budget";

// ---------------------------------------------------------------------------
// Presupuesto por periodo (Desde / Hasta).
//
//  · Meses CERRADOS del rango: presupuesto contra lo gastado de verdad.
//  · Mes en curso y meses POR VENIR: pronóstico con dos señales —
//      tendencia  = el ritmo reciente por categoría (misma regla que la
//                   proyección del año y el ajuste semanal, `categoryRunRates`);
//      año pasado = lo gastado en ese mismo mes un año antes, ajustado por
//                   cuánto ha crecido el gasto (últimos 6 meses cerrados contra
//                   los mismos 6 del año anterior, acotado entre ×0.5 y ×2).
//    Con las dos, el pronóstico es su promedio; con una sola, esa. Se decide
//    por categoría: una que no tuvo gasto ese mes del año pasado usa su tendencia.
//  · Certeza (%): sube con la estabilidad de los últimos meses, con qué tanto
//    coinciden tendencia y año pasado y con cuántos meses de historia hay;
//    baja 4 % por cada mes hacia adelante. Sin el año pasado se topa en 75 %.
//
// Lógica pura, sin I/O. Todo en moneda base (MXN).
// ---------------------------------------------------------------------------

type SpentLike = { period: string; category: string; amountBase: number | null | undefined };

export type PeriodPhase = "past" | "current" | "future";

/** Meses cerrados que se miran para estabilidad, crecimiento e historia. */
export const FORECAST_LOOKBACK = 6;
/** Historia que hay que pedir para pronosticar: 6 meses y sus mismos 6 del año anterior. */
export const FORECAST_HISTORY_MONTHS = 18;
/** Rango máximo del filtro. */
export const BUDGET_PERIOD_MAX_MONTHS = 36;
/** Qué tan adelante se puede pedir un pronóstico. */
export const BUDGET_PERIOD_MAX_AHEAD = 24;

// Solo años de este siglo: una URL editada a mano («0001-01») no pide dos mil años de gastos.
const PERIOD = /^20\d{2}-(0[1-9]|1[0-2])$/;
const round = (n: number) => Math.round(n * 100) / 100;
const idx = (p: string) => Number(p.slice(0, 4)) * 12 + Number(p.slice(5, 7)) - 1;
const at = (i: number) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
export const shiftPeriod = (p: string, delta: number) => at(idx(p) + delta);

export interface BudgetPeriodRange {
  from: string;
  to: string;
}

/** Por defecto: el año en curso completo (así se ve lo real y lo que viene). */
export function defaultBudgetPeriod(currentMonth: string): BudgetPeriodRange {
  const y = currentMonth.slice(0, 4);
  return { from: `${y}-01`, to: `${y}-12` };
}

/** Rango válido: meses bien formados, `from` ≤ `to`, máximo 36 meses y hasta 24 meses adelante. */
export function normalizeBudgetPeriod(r: Partial<BudgetPeriodRange>, currentMonth: string): BudgetPeriodRange {
  const def = defaultBudgetPeriod(currentMonth);
  let to = PERIOD.test(String(r.to || "")) ? (r.to as string) : def.to;
  const maxTo = shiftPeriod(currentMonth, BUDGET_PERIOD_MAX_AHEAD);
  if (to > maxTo) to = maxTo;
  let from = PERIOD.test(String(r.from || "")) ? (r.from as string) : def.from <= to ? def.from : to;
  if (from > to) from = to;
  if (idx(to) - idx(from) + 1 > BUDGET_PERIOD_MAX_MONTHS) from = shiftPeriod(to, -(BUDGET_PERIOD_MAX_MONTHS - 1));
  return { from, to };
}

/** Qué meses pedir al resumen para poder calcular el rango (incluye la historia del pronóstico). */
export function budgetPeriodFetchRange(r: BudgetPeriodRange, currentMonth: string): BudgetPeriodRange {
  if (r.to < currentMonth) return { from: r.from, to: r.to };
  const history = shiftPeriod(currentMonth, -FORECAST_HISTORY_MONTHS);
  return { from: history < r.from ? history : r.from, to: currentMonth };
}

export interface PeriodMonth {
  period: string;
  phase: PeriodPhase;
  budget: number;
  /** Gastado real: meses cerrados y lo que va del mes en curso; null en meses por venir. */
  actual: number | null;
  /** Ritmo reciente (null en meses cerrados o sin historia). */
  trend: number | null;
  /** Lo gastado en el mismo mes del año pasado, sin ajustar (null si no hay dato). */
  lastYear: number | null;
  /** Mismo mes del año pasado × crecimiento (null si no hay dato). */
  seasonal: number | null;
  /** Gasto esperado del mes (null en meses cerrados o sin datos para pronosticar). */
  forecast: number | null;
  low: number | null;
  high: number | null;
  /** 0–100; null si no hay pronóstico. */
  certainty: number | null;
  /** Presupuesto − (real en cerrados, pronóstico en el resto). */
  difference: number | null;
}

export interface PeriodCategoryRow {
  category: ExpenseCategory;
  budget: number;
  actual: number;
  difference: number;
  /** % ejecutado; null si no hay presupuesto. */
  pct: number | null;
}

export interface BudgetPeriodAnalysis {
  from: string;
  to: string;
  months: PeriodMonth[];
  /** Meses cerrados del rango: presupuesto contra real. */
  past: { months: number; budget: number; actual: number; difference: number; pct: number | null; byCategory: PeriodCategoryRow[] };
  /** Mes en curso y por venir: presupuesto contra pronóstico. */
  future: {
    months: number;
    budget: number;
    forecast: number | null;
    low: number | null;
    high: number | null;
    /** Certeza del pronóstico del tramo, ponderada por monto. */
    certainty: number | null;
    difference: number | null;
    byCategory: (Omit<PeriodCategoryRow, "actual"> & { forecast: number })[];
  };
  /** Crecimiento usado para ajustar el año pasado (1 = sin cambio); null si no se pudo calcular. */
  growth: number | null;
  /** Meses cerrados con gasto que respaldan la tendencia. */
  historyMonths: number;
  /** Hay algún mes del año pasado con gasto contra el cual comparar. */
  hasLastYear: boolean;
}

function spendByMonth(expenses: readonly SpentLike[]): Map<string, Partial<Record<ExpenseCategory, number>>> {
  const out = new Map<string, Partial<Record<ExpenseCategory, number>>>();
  for (const e of expenses) {
    if (e.amountBase == null || !Number.isFinite(e.amountBase)) continue;
    const cat = (EXPENSE_CATEGORIES as readonly string[]).includes(e.category) ? (e.category as ExpenseCategory) : "otros";
    const m = out.get(e.period) || {};
    m[cat] = (m[cat] || 0) + e.amountBase;
    out.set(e.period, m);
  }
  return out;
}

const sum = (m?: Partial<Record<ExpenseCategory, number>>) => (m ? Object.values(m).reduce((s, v) => s + (v || 0), 0) : 0);
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export function budgetPeriodAnalysis(lines: readonly BudgetLine[], expenses: readonly SpentLike[], range: BudgetPeriodRange, today: string): BudgetPeriodAnalysis {
  const current = today.slice(0, 7);
  const spend = spendByMonth(expenses);
  const total = (p: string) => sum(spend.get(p));

  // Tendencia por categoría: la regla compartida de ritmo.
  const spendMonths = [...spend.entries()].map(([period, byCategory]) => ({ period, total: sum(byCategory), fixed: 0, variable: 0, byCategory }) satisfies CategorySpendMonth);
  const rates = categoryRunRates(spendMonths, today);
  const trendBy = (c: ExpenseCategory) => rates[c]?.rate || 0;
  const trendTotal = round(EXPENSE_CATEGORIES.reduce((s, c) => s + trendBy(c), 0));

  // Historia reciente: los últimos 6 meses cerrados con gasto.
  const recent = Array.from({ length: FORECAST_LOOKBACK }, (_, i) => shiftPeriod(current, -1 - i));
  const recentTotals = recent.map(total).filter((v) => v > 0);
  const historyMonths = recentTotals.length;
  const mean = historyMonths ? recentTotals.reduce((s, v) => s + v, 0) / historyMonths : 0;
  const sd = historyMonths > 1 ? Math.sqrt(recentTotals.reduce((s, v) => s + (v - mean) ** 2, 0) / (historyMonths - 1)) : 0;
  const stability = historyMonths > 1 && mean > 0 ? 1 - Math.min(1, sd / mean) : 0;
  const coverage = historyMonths / FORECAST_LOOKBACK;

  // Crecimiento: meses recientes que también tienen dato un año antes.
  const pairs = recent.map((p) => [total(p), total(shiftPeriod(p, -12))] as const).filter(([a, b]) => a > 0 && b > 0);
  const growth = pairs.length ? clamp(pairs.reduce((s, [a]) => s + a, 0) / pairs.reduce((s, [, b]) => s + b, 0), 0.5, 2) : null;

  const months: PeriodMonth[] = [];
  const pastCat = new Map<ExpenseCategory, { budget: number; actual: number }>();
  const futureCat = new Map<ExpenseCategory, { budget: number; forecast: number }>();
  let hasLastYear = false;

  for (let i = idx(range.from); i <= idx(range.to); i++) {
    const period = at(i);
    const phase: PeriodPhase = period < current ? "past" : period === current ? "current" : "future";
    const budgetLines = budgetLinesFor(lines, period);
    const budget = round(budgetLines.reduce((s, l) => s + l.amount, 0));
    const actualBy = spend.get(period) || {};

    if (phase === "past") {
      const actual = round(sum(actualBy));
      for (const c of EXPENSE_CATEGORIES) {
        const b = budgetLines.filter((l) => l.category === c).reduce((s, l) => s + l.amount, 0);
        const a = actualBy[c] || 0;
        if (!b && !a) continue;
        const row = pastCat.get(c) || { budget: 0, actual: 0 };
        pastCat.set(c, { budget: row.budget + b, actual: row.actual + a });
      }
      months.push({ period, phase, budget, actual, trend: null, lastYear: null, seasonal: null, forecast: null, low: null, high: null, certainty: null, difference: round(budget - actual) });
      continue;
    }

    // El mismo mes del año pasado solo cuenta si ya cerró (a 12 meses exactos sería el mes en curso, a medias).
    const lastPeriod = shiftPeriod(period, -12);
    const lastBy = lastPeriod < current ? spend.get(lastPeriod) : undefined;
    const lastYear = lastBy && sum(lastBy) > 0 ? round(sum(lastBy)) : null;
    const g = growth ?? 1;
    const seasonal = lastYear != null ? round(lastYear * g) : null;
    if (lastYear != null) hasLastYear = true;

    // Pronóstico por categoría: promedio de las señales que existan.
    let forecast = 0;
    const forecastBy: Partial<Record<ExpenseCategory, number>> = {};
    for (const c of EXPENSE_CATEGORIES) {
      const t = trendBy(c);
      // Una categoría sin gasto ese mes del año pasado (p. ej. una suscripción nueva) no tiene
      // señal estacional: se pronostica solo con su tendencia, no a la mitad.
      const s = lastYear != null && (lastBy?.[c] || 0) > 0 ? (lastBy?.[c] || 0) * g : null;
      let f = Math.max(0, s != null ? (t > 0 ? (t + s) / 2 : s) : t);
      // El mes en curso no puede cerrar por debajo de lo que ya se gastó.
      if (phase === "current") f = Math.max(f, actualBy[c] || 0);
      if (f > 0) forecastBy[c] = f;
      forecast += f;
    }
    const hasData = trendTotal > 0 || seasonal != null || (phase === "current" && sum(actualBy) > 0);

    let certainty: number | null = null;
    if (hasData && forecast > 0) {
      const ahead = idx(period) - idx(current);
      const agreement = seasonal != null && trendTotal > 0 ? 1 - Math.min(1, Math.abs(trendTotal - seasonal) / Math.max(trendTotal, seasonal)) : null;
      const base = agreement != null ? 0.4 * stability + 0.25 * coverage + 0.35 * agreement : (0.6 * stability + 0.4 * coverage) * 0.75;
      certainty = Math.round(clamp(100 * base * Math.max(0.5, 1 - 0.04 * ahead), 5, 95));
    }
    const spread = sd > 0 ? sd : forecast * 0.25;

    for (const c of EXPENSE_CATEGORIES) {
      const b = budgetLines.filter((l) => l.category === c).reduce((s, l) => s + l.amount, 0);
      const f = forecastBy[c] || 0;
      if (!b && !f) continue;
      const row = futureCat.get(c) || { budget: 0, forecast: 0 };
      futureCat.set(c, { budget: row.budget + b, forecast: row.forecast + f });
    }

    const ok = hasData && forecast > 0;
    months.push({
      period,
      phase,
      budget,
      actual: phase === "current" ? round(sum(actualBy)) : null,
      trend: trendTotal > 0 ? trendTotal : null,
      lastYear,
      seasonal,
      forecast: ok ? round(forecast) : null,
      low: ok ? round(Math.max(phase === "current" ? sum(actualBy) : 0, forecast - spread)) : null,
      high: ok ? round(forecast + spread) : null,
      certainty,
      difference: ok ? round(budget - forecast) : null,
    });
  }

  const past = months.filter((m) => m.phase === "past");
  const rest = months.filter((m) => m.phase !== "past");
  const pastBudget = round(past.reduce((s, m) => s + m.budget, 0));
  const pastActual = round(past.reduce((s, m) => s + (m.actual || 0), 0));
  const withForecast = rest.filter((m) => m.forecast != null);
  const fTotal = withForecast.reduce((s, m) => s + (m.forecast as number), 0);
  const futureBudget = round(rest.reduce((s, m) => s + m.budget, 0));
  const order = (a: { budget: number }, b: { budget: number }) => b.budget - a.budget;

  return {
    from: range.from,
    to: range.to,
    months,
    past: {
      months: past.length,
      budget: pastBudget,
      actual: pastActual,
      difference: round(pastBudget - pastActual),
      pct: pastBudget > 0 ? round((pastActual / pastBudget) * 100) : null,
      byCategory: [...pastCat.entries()]
        .map(([category, r]) => ({ category, budget: round(r.budget), actual: round(r.actual), difference: round(r.budget - r.actual), pct: r.budget > 0 ? round((r.actual / r.budget) * 100) : null }))
        .sort((a, b) => order(a, b) || b.actual - a.actual),
    },
    future: {
      months: rest.length,
      budget: futureBudget,
      forecast: withForecast.length ? round(fTotal) : null,
      low: withForecast.length ? round(withForecast.reduce((s, m) => s + (m.low as number), 0)) : null,
      high: withForecast.length ? round(withForecast.reduce((s, m) => s + (m.high as number), 0)) : null,
      certainty: fTotal > 0 ? Math.round(withForecast.reduce((s, m) => s + (m.certainty || 0) * (m.forecast as number), 0) / fTotal) : null,
      difference: withForecast.length ? round(futureBudget - fTotal) : null,
      byCategory: [...futureCat.entries()]
        .map(([category, r]) => ({ category, budget: round(r.budget), forecast: round(r.forecast), difference: round(r.budget - r.forecast), pct: r.budget > 0 ? round((r.forecast / r.budget) * 100) : null }))
        .sort((a, b) => order(a, b) || b.forecast - a.forecast),
    },
    growth: growth != null ? round(growth) : null,
    historyMonths,
    hasLastYear,
  };
}

const URL_FROM = "desde";
const URL_TO = "hasta";

export function budgetPeriodFromQuery(search: string, currentMonth: string): BudgetPeriodRange {
  const q = new URLSearchParams(search);
  return normalizeBudgetPeriod({ from: q.get(URL_FROM) || undefined, to: q.get(URL_TO) || undefined }, currentMonth);
}

/** Query string con solo lo que difiere del valor por defecto; conserva otros parámetros. */
export function budgetPeriodToQuery(r: BudgetPeriodRange, currentMonth: string, existing = ""): string {
  const q = new URLSearchParams(existing);
  const def = defaultBudgetPeriod(currentMonth);
  // Fuera del valor por defecto van los DOS extremos: un enlace con solo uno
  // cambiaría de significado al abrirlo otro año (el defecto es el año en curso).
  if (r.from !== def.from || r.to !== def.to) {
    q.set(URL_FROM, r.from);
    q.set(URL_TO, r.to);
  } else {
    q.delete(URL_FROM);
    q.delete(URL_TO);
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
